import type {UpdateUserDataADto, UserAnalyticsADto} from '@cohort/admin-schemas/userAnalytics';
import type {MembershipPassCard as MembershipPassCardType} from '@cohort/components-xps/components/cards/MembershipPassCard';
import MembershipPassCard from '@cohort/components-xps/components/cards/MembershipPassCard';
import Button from '@cohort/merchants/components/buttons/Button';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {usersKeys} from '@cohort/merchants/hooks/api/Users';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {useCurrentUser} from '@cohort/merchants/hooks/contexts/currentUser';
import {updateUser} from '@cohort/merchants/lib/api/Users';
import EditableField from '@cohort/merchants/pages/users/user/overview/EditableField';
import {notifyError} from '@cohort/merchants/stores/ErrorModalStore';
import type {WebappLanguage} from '@cohort/shared/schema/common';
import {DateSchema, DEFAULT_BACKGROUND_COLOR} from '@cohort/shared/schema/common';
import {formatDate} from '@cohort/shared/utils/format';
import {getImageUrl, Sizes} from '@cohort/shared/utils/media';
import {zodResolver} from '@hookform/resolvers/zod';
import {ArrowsClockwise, At, Cube, Gift, TrendUp, UserPlus} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import type {TFunction} from 'i18next';
import i18n from 'i18next';
import {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {z} from 'zod';

export const UpdateUserFormSchema = z.object({
  subscriptionDate: DateSchema,
});
export type UpdateUserForm = z.infer<typeof UpdateUserFormSchema>;

type DetailRowProps = {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  action?: React.ReactNode;
};

const DetailRow: React.FC<DetailRowProps> = ({icon, label, children, action}) => (
  <div className="flex h-20 items-center space-x-4 rounded-lg border border-slate-200 p-4">
    {icon}
    <div className="flex flex-col justify-center space-y-2">
      <h3 className="text-sm text-slate-500">{label}</h3>
      <div className="flex w-full items-center justify-between">
        <div className="text-sm font-semibold">{children}</div>
        {action}
      </div>
    </div>
  </div>
);

type EmailDetailRowProps = {
  email: string;
  t: TFunction;
};

const EmailDetailRow: React.FC<EmailDetailRowProps> = ({email, t}) => (
  <DetailRow
    icon={<At className="h-10 w-10 rounded-md bg-pink-100 p-2 text-pink-500" />}
    label={t('labelEmail')}
  >
    {email}
  </DetailRow>
);

type LastActiveAtDetailRowProps = {
  lastActiveAt: Date;
  t: TFunction;
};

const LastActiveAtDetailRow: React.FC<LastActiveAtDetailRowProps> = ({lastActiveAt, t}) => (
  <DetailRow
    icon={<ArrowsClockwise className="h-10 w-10 rounded-md bg-orange-100 p-2 text-orange-500" />}
    label={t('labelLastActiveAt')}
  >
    {formatDate(lastActiveAt)}
  </DetailRow>
);

type CompletedJourneysDetailRowProps = {
  countCompletedJourneys: number;
  countJourneys: number;
  t: TFunction;
};

const CompletedJourneysDetailRow: React.FC<CompletedJourneysDetailRowProps> = ({
  countCompletedJourneys,
  countJourneys,
  t,
}) => (
  <DetailRow
    icon={<TrendUp className="h-10 w-10 rounded-md bg-blue-100 p-2 text-blue-500" />}
    label={t('labelCompletedJouneys')}
  >
    {`${countCompletedJourneys}/${countJourneys}`}
  </DetailRow>
);

type UsedPerksDetailRowProps = {
  countUsedPerks: number;
  countPerks: number;
  t: TFunction;
};

const UsedPerksDetailRow: React.FC<UsedPerksDetailRowProps> = ({countUsedPerks, countPerks, t}) => (
  <DetailRow
    icon={<Gift className="h-10 w-10 rounded-md bg-yellow-100 p-2 text-yellow-500" />}
    label={t('labelUsedPerks')}
  >
    {`${countUsedPerks}/${countPerks}`}
  </DetailRow>
);

type SubscriptionDetailRowProps = {
  user: UserAnalyticsADto;
  t: TFunction;
  setCanSave: (canSave: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
};

const SubscriptionDetailRow: React.FC<SubscriptionDetailRowProps> = ({
  user,
  t,
  setCanSave,
  setIsLoading,
}) => {
  const merchant = useCurrentMerchant();
  const queryClient = useQueryClient();

  const {register, handleSubmit, control, formState, reset} = useForm<UpdateUserForm>({
    resolver: zodResolver(UpdateUserFormSchema),
    defaultValues: {
      subscriptionDate: user.joinedAt,
    },
  });

  const {mutate: updateUserMutation, isLoading} = useCohortMutation({
    mutationFn: async (data: UpdateUserDataADto) => updateUser(merchant.id, user.id, data),
    notifySuccessMessage: t('notificationSuccess'),
    onSuccess: async () => {
      reset({}, {keepValues: true});
      await queryClient.invalidateQueries(usersKeys.users);
    },
    onError: err => notifyError(err, t('notificationError')),
  });

  useEffect(() => {
    setCanSave(formState.isDirty);
  }, [formState.isDirty, setCanSave]);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  return (
    <form
      id="user-form"
      onSubmit={handleSubmit(data => updateUserMutation({membershipPass: data}))}
    >
      <DetailRow
        icon={<UserPlus className="h-10 w-10 rounded-md bg-purple-100 p-2 text-purple-500" />}
        label={t('labelJoinedAt')}
      >
        <EditableField
          dataType="date"
          control={control}
          register={register}
          name="subscriptionDate"
          defaultValue={user.joinedAt}
          key={user.joinedAt.toString()}
        />
      </DetailRow>
    </form>
  );
};

type DigitalAssetsDetailRowProps = {
  countDigitalAssets: number;
  t: TFunction;
};

const DigitalAssetsDetailRow: React.FC<DigitalAssetsDetailRowProps> = ({countDigitalAssets, t}) => (
  <DetailRow
    icon={<Cube className="h-10 w-10 rounded-md bg-green-100 p-2 text-green-500" />}
    label={t('labelDigitalAssets')}
  >
    {`${countDigitalAssets}`}
  </DetailRow>
);

const UserInformationSection = (): JSX.Element => {
  const user = useCurrentUser();
  const merchant = useCurrentMerchant();
  const {t} = useTranslation('pages', {keyPrefix: 'users.user.overview.userInformationSection'});
  const selectedLanguage = i18n.language as WebappLanguage;

  const [canSave, setCanSave] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getMembershipPassBannerUrl = (): string | null => {
    if (merchant.membershipPassConfiguration.bannerFileKey) {
      return getImageUrl(
        import.meta.env.COHORT_ENV,
        merchant.membershipPassConfiguration.bannerFileKey,
        {
          h: Sizes.S,
          w: Sizes.S,
        }
      );
    } else if (merchant.logoFileKey) {
      return getImageUrl(import.meta.env.COHORT_ENV, merchant.logoFileKey, {
        h: Sizes.S,
        w: Sizes.S,
      });
    }
    return null;
  };

  const membershipPassCard = {
    title:
      merchant.membershipPassConfiguration.title[selectedLanguage] ??
      t('defaultTitle', {merchantName: merchant.name}),
    imageUrl: getMembershipPassBannerUrl(),
    backgroundColorCode:
      merchant.membershipPassConfiguration.backgroundColorCode ??
      merchant.backgroundColorCode ??
      DEFAULT_BACKGROUND_COLOR,
    experienceSpaceBackgroundColorCode: merchant.backgroundColorCode ?? DEFAULT_BACKGROUND_COLOR,
    memberHandle: user.membershipPassHandle,
    subscriptionDate: user.joinedAt,
    memberId: user.membershipPassMemberId,
  } satisfies MembershipPassCardType;

  return (
    <div className="flex flex-col space-y-6">
      <div className="flex w-full space-x-6">
        <div className="grid flex-grow grid-cols-2 gap-6">
          <EmailDetailRow email={user.email} t={t} />
          <SubscriptionDetailRow
            user={user}
            t={t}
            setCanSave={setCanSave}
            setIsLoading={setIsLoading}
          />
          <LastActiveAtDetailRow lastActiveAt={user.lastActiveAt} t={t} />
          <CompletedJourneysDetailRow
            countCompletedJourneys={user.countCompletedJourneys}
            countJourneys={user.countJourneys}
            t={t}
          />
          <UsedPerksDetailRow
            countUsedPerks={user.countUsedPerks}
            countPerks={user.countPerks}
            t={t}
          />
          <DigitalAssetsDetailRow countDigitalAssets={user.countDigitalAssets} t={t} />
        </div>
        <div className="w-[300px]">
          <MembershipPassCard
            membershipPassCard={membershipPassCard}
            selectedLanguage={selectedLanguage}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button form="user-form" type="submit" disabled={canSave === false} loading={isLoading}>
          {t('buttonSave')}
        </Button>
      </div>
    </div>
  );
};

export default UserInformationSection;
